/*
 * Copied unchanged from Craft Together
 * (`client/src/modes/human-human/micCheckWindow.ts`, commit e7540a5), along
 * with its test suite. It encodes a fix for a real incident in the Aug-3 study
 * and there is nothing about it that is specific to that game — reproducing it
 * from memory would only reproduce the bug.
 *
 * Only this header is new.
 */

/**
 * Pure logic for the solo microphone check's "we heard you" decision
 * (SoloMicCheck.tsx).
 *
 * The original rule required the mean-FFT level to stay at or above the
 * threshold CONTINUOUSLY for 800ms — a single sub-threshold frame reset
 * the accumulator. Natural speech is not continuous: the gaps between
 * words, and the low-energy tail of a quiet or distant microphone, drop
 * below threshold constantly. On a quiet mic the rule was effectively
 * unpassable, and because the phase has an idle cap (STUDY_VOICE_IDLE_MS,
 * 3 min in production) a participant who was in fact speaking got ejected
 * with an idle completion code. That happened to a real participant in
 * the Aug-3 study.
 *
 * The rule here instead ACCUMULATES above-threshold time inside a
 * sliding window: 800ms of speech within any 3s stretch passes. Brief
 * dips cost only the silent frames themselves, they never reset progress.
 *
 * Each frame's contribution is the gap until the next frame (or now for
 * the newest frame), capped at MAX_FRAME_SPAN_MS so a stalled render
 * loop — a backgrounded tab, a long GC pause — cannot let one loud frame
 * count for seconds of imaginary speech.
 */

/** Mean-FFT level (0-100 scale, as rendered by the meter) at or above
 * which a frame counts as speech. Unchanged from the original rule. */
export const MIC_LEVEL_THRESHOLD = 12;

/** Above-threshold time that must accumulate inside the window to pass. */
export const MIC_REQUIRED_ACTIVE_MS = 800;

/** Length of the sliding window the active time accumulates within. */
export const MIC_WINDOW_MS = 3000;

/** Maximum time a single frame may be credited with. Bounds the damage
 * from a stalled animation frame. ~4 missed frames at 60fps. */
export const MAX_FRAME_SPAN_MS = 250;

/** How long the participant may sit in the listening state before we
 * offer a manual "my microphone is working" override. Well inside the
 * production mic-check idle cap (STUDY_VOICE_IDLE_MS = 180000). */
export const MIC_MANUAL_OVERRIDE_AFTER_MS = 45000;

export interface MicFrame {
  /** performance.now() timestamp of the sample. */
  at: number;
  /** Whether the sample was at or above MIC_LEVEL_THRESHOLD. */
  active: boolean;
}

/** Append a sample and drop frames that can no longer contribute to the
 * window. Returns a new array; never mutates the input. */
export function pushFrame(
  frames: readonly MicFrame[],
  at: number,
  level: number,
  windowMs: number = MIC_WINDOW_MS,
): MicFrame[] {
  const next = [...frames, { at, active: level >= MIC_LEVEL_THRESHOLD }];
  // Keep one frame's worth of slack before the window start: a frame
  // that begins just before the window can still contribute the part of
  // its span that falls inside it.
  const cutoff = at - windowMs - MAX_FRAME_SPAN_MS;
  let first = 0;
  while (first < next.length && next[first].at < cutoff) first++;
  return first > 0 ? next.slice(first) : next;
}

/** Total above-threshold milliseconds inside the trailing window. */
export function activeMsInWindow(
  frames: readonly MicFrame[],
  now: number,
  windowMs: number = MIC_WINDOW_MS,
): number {
  const windowStart = now - windowMs;
  let total = 0;
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    if (!frame.active) continue;
    const spanEnd = i + 1 < frames.length ? frames[i + 1].at : now;
    const start = Math.max(frame.at, windowStart);
    const end = Math.min(spanEnd, now, frame.at + MAX_FRAME_SPAN_MS);
    if (end > start) total += end - start;
  }
  return total;
}

/** Whether enough speech has accumulated in the window to pass. */
export function hasHeardEnough(
  frames: readonly MicFrame[],
  now: number,
  windowMs: number = MIC_WINDOW_MS,
): boolean {
  return activeMsInWindow(frames, now, windowMs) >= MIC_REQUIRED_ACTIVE_MS;
}

/** Whether the manual "my microphone is working" override should be
 * offered yet, given when the listening state started. */
export function shouldOfferManualOverride(listeningSinceMs: number, now: number): boolean {
  return now - listeningSinceMs >= MIC_MANUAL_OVERRIDE_AFTER_MS;
}
